import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const HowItWorksSection = () => {
  const steps = [
    {
      number: 1,
      icon: 'Wind',
      title: 'Warmte uit de omgeving',
      description: 'De warmtepomp haalt warmte uit de buitenlucht, bodem of grondwater - zelfs bij vriestemperaturen.'
    },
    {
      number: 2,
      icon: 'Zap',
      title: 'Compressie verhoogt temperatuur',
      description: 'Een compressor verhoogt de temperatuur van de warmte met behulp van elektriciteit.'
    },
    {
      number: 3,
      icon: 'Thermometer',
      title: 'Warmte naar verwarmingssysteem',
      description: 'De verhoogde warmte wordt overgedragen aan uw vloerverwarming of radiatoren.'
    },
    {
      number: 4,
      icon: 'RefreshCw',
      title: 'Cyclus herhaalt zich',
      description: 'Het proces herhaalt zich continu voor een constante, comfortabele temperatuur.'
    }
  ];

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-4">
            Hoe werkt een warmtepomp?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Een warmtepomp werkt als een omgekeerde koelkast: het haalt warmte uit de omgeving en brengt deze naar binnen.
          </p>
        </motion.div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-cyan-300 to-blue-200 -translate-y-1/2 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps?.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="relative"
              >
                <div className="bg-card border-2 border-border rounded-xl p-6 shadow-elevation-1 hover:shadow-elevation-3 transition-all h-full">
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-elevation-2">
                    {step?.number}
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon name={step?.icon} size={32} className="text-blue-600" />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-3 text-center">
                    {step?.title}
                  </h3>
                  <p className="text-sm text-muted-foreground text-center">
                    {step?.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Efficiency Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 lg:p-8"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="Lightbulb" size={24} className="text-white" />
            </div>
            <div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">Waarom zo efficiënt?</h3>
              <p className="text-muted-foreground mb-4">
                Een warmtepomp gebruikt 1 kWh elektriciteit om 3-5 kWh warmte te produceren. Dit komt omdat het warmte <strong>verplaatst</strong> in plaats van opwekt. Dit maakt het veel efficiënter dan traditionele cv-ketels.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-2xl font-bold text-green-600 mb-1">COP 4.0</p>
                  <p className="text-sm text-muted-foreground">1 kWh → 4 kWh warmte</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-2xl font-bold text-green-600 mb-1">300%</p>
                  <p className="text-sm text-muted-foreground">Efficiënter dan gas</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-green-200">
                  <p className="text-2xl font-bold text-green-600 mb-1">0 kg</p>
                  <p className="text-sm text-muted-foreground">CO₂ met groene stroom</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;